import seedrandom from 'seedrandom';
import type { DataValue } from '../sources';

// Кеш для сохранения порядка элементов между вызовами
const orderCache = new Map<string, number>();
const MAX_UINT32 = 0xffffffff;
const RANDOM_SCALE_FACTOR = 0x100000000;
let sessionSeed = String(Date.now());

export function clearSortCache() {
  orderCache.clear();
  sessionSeed = `${Date.now()}|${Math.floor(Math.random() * MAX_UINT32)}`;
}

function makeUniqueString(item: DataValue) {
  return [
    item.source?.url ?? '',
    item.text ?? '',
    item.timestamp?.toString() ?? '',
    item.source?.type ?? ''
  ].join('|');
}

// Присваиваем order для элемента при первом появлении
function assignOrderIfMissing(item: DataValue) {
  const idKey = item.id ?? makeUniqueString(item);
  if (orderCache.has(idKey)) {
    return orderCache.get(idKey);
  }

  // Детерминированный RNG на основе sessionSeed + уникального ключа
  const randomGenerator = seedrandom(`${sessionSeed}|${idKey}`);
  // Получаем 32-битное целое для хорошего распределения
  const orderValue = Math.floor(randomGenerator() * RANDOM_SCALE_FACTOR);
  orderCache.set(idKey, orderValue);

  return orderValue;
}

// Стабильная рандомная сортировка постов с сохранением порядка уже отсортированных элементов
// Порядок будет одинаковым для одного и того же набора данных
export function stableRandomSort(items: DataValue[]) {
  // Присваиваем order для всех элементов, которые ещё не в кеше
  items.forEach((item) => assignOrderIfMissing(item));

  // Копируем и сортируем по сохранённому order
  return [...items].sort((firstItem, secondItem) => {
    const firstItemKey = firstItem.id ?? makeUniqueString(firstItem);
    const secondItemKey = secondItem.id ?? makeUniqueString(secondItem);

    const firstItemOrder = orderCache.get(firstItemKey)!;
    const secondItemOrder = orderCache.get(secondItemKey)!;

    if (firstItemOrder !== secondItemOrder) {
      return firstItemOrder - secondItemOrder;
    }

    const timestampA = firstItem.timestamp ?? 0;
    const timestampB = secondItem.timestamp ?? 0;
    if (timestampA !== timestampB) {
      return timestampB - timestampA;
    }

    if (firstItemKey < secondItemKey) {
      return -1;
    }

    if (firstItemKey > secondItemKey) {
      return 1;
    }

    return 0;
  });
}
